import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { useViewMode } from '../contexts/ViewModeContext';
import AdminLeaveManagement from './AdminLeaveManagement';
import LeaveCalendar from './LeaveCalendar';
import FestivalSuggestionsAdmin from './FestivalSuggestionsAdmin';
import CustomizeBalancesTab from './admin/CustomizeBalancesTab';
import {
  Plus, Edit, Trash2, Eye, X, Briefcase, Building2, Users,
  FileText, DollarSign, Clock, Calendar, Send, MessageSquare,
  CheckCircle, XCircle, AlertCircle, Loader2, Search, Filter,
  ChevronRight, Link as LinkIcon, Paperclip, Gift, LayoutGrid, Table, Download,
  ChevronDown
} from 'lucide-react';
import ApplicationsTable from './hr/ApplicationsTable';
import { getTodayLocal } from '../lib/dateUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { formatDate, formatDateTime, formatRelativeTime } from '../lib/dateUtils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API_URL = '';

export default function HumanResourcesPage() {
  const { isMobileView } = useViewMode();
  const [activeTab, setActiveTab] = useState('roles');
  const [departments, setDepartments] = useState([]);
  
  // Hiring Roles State
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    designation: '',
    department_id: '',
    qualifications: '',
    skills: '',
    scope_of_work: '',
    is_published: false
  });
  const [savingRole, setSavingRole] = useState(false);
  
  // Applications State
  const [applications, setApplications] = useState([]);
  // Card | Table view toggle for the applications list (persisted)
  const [appsViewMode, setAppsViewMode] = useState(() => {
    try { return localStorage.getItem('apps_view_mode') || 'card'; } catch (e) { return 'card'; }
  });
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsStats, setAppsStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');  // role designation
  const [dateFromFilter, setDateFromFilter] = useState('');  // YYYY-MM-DD
  const [dateToFilter, setDateToFilter] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Application Detail Modal
  const [showAppDetail, setShowAppDetail] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  const [deletingApp, setDeletingApp] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await adminApi.get('/api/departments');
        setDepartments(response.data || []);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const response = await adminApi.get('/api/admin/hiring-roles');
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch hiring roles');
    } finally {
      setRolesLoading(false);
    }
  }, []);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    setAppsLoading(true);
    try {
      const [appsRes, statsRes] = await Promise.all([
        adminApi.get('/api/admin/job-applications'),
        adminApi.get('/api/admin/job-applications/stats')
      ]);
      setApplications(appsRes.data || []);
      setAppsStats(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'roles') {
      fetchRoles();
    } else if (activeTab === 'applications') {
      fetchApplications();
    }
  }, [activeTab, fetchRoles, fetchApplications]);

  // ========== Hiring Roles Functions ==========
  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({
      designation: '',
      department_id: '',
      qualifications: '',
      skills: '',
      scope_of_work: '',
      is_published: false
    });
    setShowRoleModal(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      designation: role.designation || '',
      department_id: role.department_id || '',
      qualifications: role.qualifications || '',
      skills: role.skills || '',
      scope_of_work: role.scope_of_work || '',
      is_published: role.is_published || false
    });
    setShowRoleModal(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.designation || !roleForm.department_id) {
      toast.error('Please fill in required fields');
      return;
    }

    setSavingRole(true);
    try {
      if (editingRole) {
        await adminApi.put(`/api/admin/hiring-roles/${editingRole._id}`, roleForm);
        toast.success('Role updated successfully');
      } else {
        await adminApi.post('/api/admin/hiring-roles', roleForm);
        toast.success('Role created successfully');
      }
      setShowRoleModal(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      await adminApi.delete(`/api/admin/hiring-roles/${roleId}`);
      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const togglePublishRole = async (role) => {
    try {
      await adminApi.put(`/api/admin/hiring-roles/${role._id}`, {
        ...role,
        is_published: !role.is_published
      });
      toast.success(role.is_published ? 'Role unpublished' : 'Role published');
      fetchRoles();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  // ========== Applications Functions ==========
  const openAppDetail = async (app) => {
    try {
      const response = await adminApi.get(`/api/admin/job-applications/${app._id}`);
      setSelectedApp(response.data);
      setShowAppDetail(true);
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      toast.error('Failed to load application details');
    }
  };

  // Live chat polling (Track B) — poll the open application every 5s while
  // the detail dialog is open. Pause when the tab is hidden. Smart-diff so we
  // only re-render when something actually changed.
  useEffect(() => {
    if (!showAppDetail || !selectedApp?._id) return;
    const appId = selectedApp._id;
    let cancelled = false;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const r = await adminApi.get(`/api/admin/job-applications/${appId}`);
        if (cancelled) return;
        const fresh = r.data;
        setSelectedApp((prev) => {
          if (!prev || prev._id !== appId) return prev;
          const prevLen = (prev.messages || []).length;
          const freshLen = (fresh.messages || []).length;
          const prevLast = (prev.messages || []).slice(-1)[0]?.timestamp || '';
          const freshLast = (fresh.messages || []).slice(-1)[0]?.timestamp || '';
          if (
            prevLen !== freshLen ||
            prevLast !== freshLast ||
            prev.status !== fresh.status ||
            prev.offer_status !== fresh.offer_status ||
            prev.offer_amount !== fresh.offer_amount
          ) {
            return fresh;
          }
          return prev;
        });
      } catch (e) { /* silent — best-effort */ }
    };
    const interval = setInterval(tick, 5000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [showAppDetail, selectedApp?._id]);

  // Auto-scroll the HR chat to bottom whenever new messages arrive via
  // polling or manual send. messagesEndRef already exists.
  useEffect(() => {
    if (!showAppDetail) return;
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, [showAppDetail, selectedApp?.messages?.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      await adminApi.post(`/api/admin/job-applications/${selectedApp._id}/messages`, {
        message: newMessage
      });
      setNewMessage('');
      // Refresh application data
      const response = await adminApi.get(`/api/admin/job-applications/${selectedApp._id}`);
      setSelectedApp(response.data);
      setTimeout(() => scrollToBottom(), 100);
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await adminApi.put(`/api/admin/job-applications/${selectedApp._id}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      // Refresh
      const response = await adminApi.get(`/api/admin/job-applications/${selectedApp._id}`);
      setSelectedApp(response.data);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExtendOffer = async () => {
    if (!offerAmount) {
      toast.error('Please enter offer amount');
      return;
    }

    setSendingOffer(true);
    try {
      await adminApi.post(`/api/admin/job-applications/${selectedApp._id}/extend-offer`, {
        amount: offerAmount
      });
      toast.success('Offer extended successfully');
      setShowOfferModal(false);
      setOfferAmount('');
      // Refresh
      const response = await adminApi.get(`/api/admin/job-applications/${selectedApp._id}`);
      setSelectedApp(response.data);
      fetchApplications();
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      toast.error('Failed to extend offer');
    } finally {
      setSendingOffer(false);
    }
  };

  // Delete application
  const handleDeleteApplication = async () => {
    if (!appToDelete) return;
    setDeletingApp(true);
    try {
      await adminApi.delete(`/api/admin/applications/${appToDelete._id}`);
      toast.success('Application deleted successfully');
      setAppToDelete(null);
      setShowAppDetail(false);
      setSelectedApp(null);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    } finally {
      setDeletingApp(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Submitted': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'In Review': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Interview': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Offer Extended': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Offer Accepted': return 'bg-green-100 text-green-700 border-green-200';
      case 'Offer Rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'Rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-neutral-100 text-neutral-700 border-white/40';
    }
  };

  // Derived lists for the department + role dropdowns (unique + sorted).
  const uniqueDepartments = Array.from(
    new Set(applications.map((a) => a.department_name).filter(Boolean))
  ).sort();
  const uniqueRoles = Array.from(
    new Set(applications.map((a) => a.designation).filter(Boolean))
  ).sort();

  const hasActiveFilters =
    statusFilter !== 'all' ||
    departmentFilter !== 'all' ||
    roleFilter !== 'all' ||
    !!dateFromFilter ||
    !!dateToFilter ||
    !!searchQuery;

  const resetFilters = () => {
    setStatusFilter('all');
    setDepartmentFilter('all');
    setRoleFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setSearchQuery('');
  };

  const filteredApplications = applications.filter((app) => {
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesDept = departmentFilter === 'all' || app.department_name === departmentFilter;
    const matchesRole = roleFilter === 'all' || app.designation === roleFilter;
    const matchesSearch = !searchQuery ||
      app.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.designation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.applicant_email?.toLowerCase().includes(searchQuery.toLowerCase());
    // Date range applies to created_at (application submission date)
    let matchesDate = true;
    if (dateFromFilter || dateToFilter) {
      const created = app.created_at ? new Date(app.created_at) : null;
      if (!created || isNaN(created.getTime())) {
        matchesDate = false;
      } else {
        if (dateFromFilter) {
          const from = new Date(dateFromFilter);
          from.setHours(0, 0, 0, 0);
          if (created < from) matchesDate = false;
        }
        if (dateToFilter) {
          const to = new Date(dateToFilter);
          to.setHours(23, 59, 59, 999);
          if (created > to) matchesDate = false;
        }
      }
    }
    return matchesStatus && matchesDept && matchesRole && matchesSearch && matchesDate;
  });

  /**
   * Download an .xlsx export of applications.
   *   scope='filtered' → sends the same status/search params that the UI is
   *                      currently applying, so what the user SEES == what they GET.
   *   scope='all'      → no filters — every application in the system.
   */
  const handleExportExcel = async (scope) => {
    setExporting(true);
    setExportOpen(false);
    try {
      const params = {};
      if (scope === 'filtered') {
        if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
        if (departmentFilter && departmentFilter !== 'all') params.department_name = departmentFilter;
        if (roleFilter && roleFilter !== 'all') params.designation = roleFilter;
        if (dateFromFilter) params.date_from = dateFromFilter;
        if (dateToFilter) params.date_to = dateToFilter;
        if (searchQuery && searchQuery.trim()) params.search = searchQuery.trim();
      }
      const res = await adminApi.get('/api/admin/job-applications/export', {
        params,
        responseType: 'blob',
      });
      // Turn the blob into a browser download
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = getTodayLocal();
      a.download = `girgit-applications-${scope}-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const rowCount = res.headers?.['x-girgit-row-count'] || '';
      toast.success(`Exported ${rowCount || ''} application${rowCount === '1' ? '' : 's'} to Excel`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to export applications');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={isMobileView ? 'px-4 py-4' : 'p-8'} data-testid="hr-page">
      {/* Header */}
      <div className={isMobileView ? 'mb-4' : 'mb-8'}>
        <h1 className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-3xl'}`}>Human Resources</h1>
        <p className={`text-neutral-600 ${isMobileView ? 'text-sm' : 'mt-1'}`}>Manage job postings and applications</p>
      </div>

      {/* Tabs - Scrollable on mobile */}
      <div className={`border-b border-white/40 ${isMobileView ? 'mb-4 overflow-x-auto -mx-4 px-4' : 'mb-8'}`}>
        <div className={`flex items-center gap-2 ${isMobileView ? 'min-w-max' : ''}`}>
          <button
            onClick={() => setActiveTab('roles')}
            data-testid="tab-hiring-roles"
            className={`font-medium border-b-2 transition-colors whitespace-nowrap ${
              isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
            } ${
              activeTab === 'roles'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'
            }`}
          >
            <Briefcase className="w-4 h-4 inline mr-1" />
            {isMobileView ? 'Roles' : 'Hiring Roles'}
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            data-testid="tab-applications"
            className={`font-medium border-b-2 transition-colors whitespace-nowrap ${
              isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
            } ${
              activeTab === 'applications'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'
            }`}
          >
            <Users className="w-4 h-4 inline mr-1" />
            {isMobileView ? 'Apps' : 'Applications'}
            {appsStats.submitted > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-pink-100 text-pink-700 rounded-full text-xs">
                {appsStats.submitted}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('leave-policy')}
            className={`font-medium border-b-2 transition-colors whitespace-nowrap ${
              isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
            } ${
              activeTab === 'leave-policy'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'
            }`}
          >
            {isMobileView ? 'Leave' : 'Leave Policy'}
          </button>
          <button
            onClick={() => setActiveTab('leave-calendar')}
            className={`font-medium border-b-2 transition-colors whitespace-nowrap ${
              isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
            } ${
              activeTab === 'leave-calendar'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'
            }`}
          >
            {isMobileView ? 'Calendar' : 'Leave Calendar'}
          </button>
          <button
            onClick={() => setActiveTab('festival-suggestions')}
            data-testid="tab-festival-suggestions"
            className={`font-medium border-b-2 transition-colors whitespace-nowrap ${
              isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
            } ${
              activeTab === 'festival-suggestions'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'
            }`}
          >
            {isMobileView ? 'Festivals' : 'Festival Suggestions'}
          </button>
          <button
            onClick={() => setActiveTab('customize-balances')}
            data-testid="tab-customize-balances"
            className={`font-medium border-b-2 transition-colors whitespace-nowrap ${
              isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
            } ${
              activeTab === 'customize-balances'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'
            }`}
          >
            {isMobileView ? 'Balances' : 'Customize Balances'}
          </button>
        </div>
      </div>

      {/* ========== HIRING ROLES TAB ========== */}
      {activeTab === 'roles' && (
        <div>
          {/* Actions Bar */}
          <div className={`flex items-center justify-between ${isMobileView ? 'mb-4 flex-col gap-2' : 'mb-6'}`}>
            <p className={`text-neutral-600 ${isMobileView ? 'text-sm' : ''}`}>{roles.length} roles configured</p>
            <Button 
              onClick={openCreateRole}
              data-testid="create-role-btn"
              className={`bg-pink-600 hover:bg-pink-700 text-white ${isMobileView ? 'w-full' : ''}`}
            >
              <Plus className="w-4 h-4 mr-2" />
              {isMobileView ? 'New Role' : 'Create New Role'}
            </Button>
          </div>

          {/* Roles Grid */}
          {rolesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-pink-600" />
            </div>
          ) : roles.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Briefcase className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#1a1a2e]">No Hiring Roles</h3>
              <p className="text-neutral-500 mt-2">Create your first role to start accepting applications</p>
              <Button 
                onClick={openCreateRole}
                className="mt-4 bg-pink-600 hover:bg-pink-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </div>
          ) : (
            <div className={`grid gap-4 ${isMobileView ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
              {roles.map(role => (
                <div 
                  key={role._id} 
                  data-testid={`role-card-${role._id}`}
                  className={`glass-card hover:shadow-lg transition-shadow ${isMobileView ? 'p-4' : 'p-6'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-base' : 'text-lg'}`}>{role.designation}</h3>
                      <p className="text-sm text-neutral-600 flex items-center gap-1 mt-1">
                        <Building2 className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{role.department_name || 'No Department'}</span>
                      </p>
                    </div>
                    <Badge 
                      className={`flex-shrink-0 ${role.is_published 
                        ? 'bg-green-100 text-green-700 border-green-200' 
                        : 'bg-neutral-100 text-neutral-600 border-white/40'
                      }`}
                    >
                      {role.is_published ? 'Published' : 'Draft'}
                    </Badge>
                  </div>

                  {role.qualifications && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-neutral-500 uppercase mb-1">Qualifications</p>
                      <p className="text-sm text-neutral-700 line-clamp-2">{role.qualifications}</p>
                    </div>
                  )}

                  {role.skills && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-neutral-500 uppercase mb-1">Skills</p>
                      <p className="text-sm text-neutral-700 line-clamp-2">{role.skills}</p>
                    </div>
                  )}

                  <div className={`flex items-center gap-2 pt-3 border-t border-white/30 ${isMobileView ? 'flex-wrap' : 'mt-4 pt-4'}`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditRole(role)}
                      className={isMobileView ? 'flex-1 min-w-[70px]' : 'flex-1'}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => togglePublishRole(role)}
                      className={`flex-1 ${role.is_published ? 'text-orange-600' : 'text-green-600'}`}
                    >
                      {role.is_published ? (
                        <>
                          <XCircle className="w-4 h-4 mr-1" />
                          Unpublish
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Publish
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteRole(role._id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== APPLICATIONS TAB ========== */}
      {activeTab === 'applications' && (
        <div>
          {/* Stats Cards */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-[#1a1a2e]">{appsStats.total || 0}</p>
              <p className="text-sm text-neutral-600">Total</p>
            </div>
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{appsStats.submitted || 0}</p>
              <p className="text-sm text-blue-600">New</p>
            </div>
            <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">{appsStats.in_review || 0}</p>
              <p className="text-sm text-yellow-600">In Review</p>
            </div>
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">{appsStats.interview || 0}</p>
              <p className="text-sm text-purple-600">Interview</p>
            </div>
            <div className="bg-green-50 rounded-xl border border-green-200 p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{appsStats.offer_accepted || 0}</p>
              <p className="text-sm text-green-600">Accepted</p>
            </div>
          </div>

          {/* Filters */}
          {/* Filters row 1: Search + Status + Department + Role */}
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <Input
                placeholder="Search by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-applications"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="Submitted">Submitted</SelectItem>
                <SelectItem value="In Review">In Review</SelectItem>
                <SelectItem value="Interview">Interview</SelectItem>
                <SelectItem value="Offer Extended">Offer Extended</SelectItem>
                <SelectItem value="Offer Accepted">Offer Accepted</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-52" data-testid="filter-department">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {uniqueDepartments.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-52" data-testid="filter-role">
                <Briefcase className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {uniqueRoles.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filters row 2: Date range + Clear + View toggle */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-neutral-500" />
              <Input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="w-44"
                data-testid="filter-date-from"
                placeholder="From"
                title="Applied on or after"
              />
              <span className="text-neutral-400 text-sm">to</span>
              <Input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="w-44"
                data-testid="filter-date-to"
                placeholder="To"
                title="Applied on or before"
              />
            </div>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
                data-testid="clear-filters-btn"
              >
                <X className="w-3.5 h-3.5" />
                Clear filters
              </button>
            )}

            <div className="flex-1" />

            {/* Card / Table view toggle */}
            <div className="inline-flex items-center bg-neutral-100 rounded-lg p-0.5" data-testid="apps-view-toggle">
              <button
                onClick={() => { setAppsViewMode('card'); try { localStorage.setItem('apps_view_mode', 'card'); } catch (e) { /* ignore */ } }}
                data-testid="apps-view-card"
                aria-pressed={appsViewMode === 'card'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-all ${
                  appsViewMode === 'card' ? 'bg-white text-pink-700 shadow-sm font-medium' : 'text-neutral-600 hover:text-neutral-800'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Cards
              </button>
              <button
                onClick={() => { setAppsViewMode('table'); try { localStorage.setItem('apps_view_mode', 'table'); } catch (e) { /* ignore */ } }}
                data-testid="apps-view-table"
                aria-pressed={appsViewMode === 'table'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-all ${
                  appsViewMode === 'table' ? 'bg-white text-pink-700 shadow-sm font-medium' : 'text-neutral-600 hover:text-neutral-800'
                }`}
              >
                <Table className="w-3.5 h-3.5" /> Table
              </button>
            </div>

            {/* Export to Excel — dropdown with Filtered vs All */}
            <div className="relative" data-testid="export-applications">
              <button
                onClick={() => setExportOpen((o) => !o)}
                disabled={exporting || appsLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 shadow-sm transition-colors"
                data-testid="export-applications-btn"
                title="Download applications as an Excel workbook"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export Excel
                <ChevronDown className="w-3.5 h-3.5 opacity-80" />
              </button>
              {exportOpen && (
                <>
                  <button
                    aria-label="Close export menu"
                    onClick={() => setExportOpen(false)}
                    className="fixed inset-0 z-30 cursor-default"
                  />
                  <div
                    className="absolute right-0 mt-2 w-72 rounded-xl border border-neutral-200 bg-white shadow-xl z-40 overflow-hidden"
                    data-testid="export-applications-menu"
                  >
                    <button
                      onClick={() => handleExportExcel('filtered')}
                      className="w-full text-left px-4 py-3 hover:bg-emerald-50 border-b border-neutral-100 group"
                      data-testid="export-filtered-btn"
                    >
                      <div className="font-medium text-neutral-900 group-hover:text-emerald-700">
                        Export filtered ({filteredApplications.length})
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        {hasActiveFilters
                          ? (() => {
                              const bits = [];
                              if (statusFilter !== 'all') bits.push(`status = ${statusFilter}`);
                              if (departmentFilter !== 'all') bits.push(`dept = ${departmentFilter}`);
                              if (roleFilter !== 'all') bits.push(`role = ${roleFilter}`);
                              if (dateFromFilter || dateToFilter) {
                                bits.push(`date ${dateFromFilter || '…'} → ${dateToFilter || '…'}`);
                              }
                              if (searchQuery) bits.push(`search "${searchQuery}"`);
                              return `Applies: ${bits.join(' · ')}`;
                            })()
                          : 'Currently no filters — same as Export all'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleExportExcel('all')}
                      className="w-full text-left px-4 py-3 hover:bg-emerald-50 group"
                      data-testid="export-all-btn"
                    >
                      <div className="font-medium text-neutral-900 group-hover:text-emerald-700">
                        Export all applications ({appsStats.total || applications.length})
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        Every application in the system, ignoring current filters
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Applications List */}
          {appsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-pink-600" />
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#1a1a2e]">No Applications</h3>
              <p className="text-neutral-500 mt-2">
                {hasActiveFilters
                  ? 'No applications match your filters'
                  : 'Applications will appear here when candidates apply'}
              </p>
            </div>
          ) : appsViewMode === 'table' ? (
            <ApplicationsTable
              applications={filteredApplications}
              onSelect={(app) => openAppDetail(app)}
              onStatusChange={async (app, newStatus) => {
                try {
                  await adminApi.put(`/api/admin/job-applications/${app._id}/status?status=${encodeURIComponent(newStatus)}`);
                  // Optimistic local update
                  setApplications((prev) => prev.map((a) => a._id === app._id ? { ...a, status: newStatus } : a));
                  toast.success(`Status updated to ${newStatus}`);
                  // Refresh stats
                  try {
                    const s = await adminApi.get('/api/admin/job-applications/stats');
                    setAppsStats(s.data || {});
                  } catch (e) { /* non-fatal */ }
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to update status');
                  throw e;
                }
              }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredApplications.map(app => (
                <div
                  key={app._id}
                  data-testid={`application-card-${app._id}`}
                  className="glass-card p-5 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => openAppDetail(app)}
                    >
                      <h3 className="font-bold text-[#1a1a2e] group-hover:text-pink-600 transition-colors">
                        {app.applicant_name}
                      </h3>
                      <p className="text-sm text-neutral-500">{app.applicant_email}</p>
                      {app.applicant_phone && (
                        <p className="text-xs text-neutral-400">{app.applicant_phone}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(app.status)}>
                        {app.status}
                      </Badge>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAppToDelete(app);
                        }}
                        className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete Application"
                        data-testid={`delete-app-${app._id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div 
                    className="space-y-2 text-sm cursor-pointer"
                    onClick={() => openAppDetail(app)}
                  >
                    <div className="flex items-center gap-2 text-neutral-600">
                      <Briefcase className="w-4 h-4" />
                      <span className="font-medium">{app.designation}</span>
                    </div>
                    <div className="flex items-center gap-2 text-neutral-600">
                      <Building2 className="w-4 h-4" />
                      <span>{app.department_name}</span>
                    </div>
                    <div className="flex items-center justify-between text-neutral-500">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{app.years_of_experience || '0'} yrs exp</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        <span>₹{app.expected_ctc}</span>
                      </div>
                    </div>
                  </div>

                  <div 
                    className="flex items-center justify-between mt-4 pt-3 border-t border-white/30 text-xs text-neutral-400 cursor-pointer"
                    onClick={() => openAppDetail(app)}
                  >
                    <span>Applied {formatRelativeTime(app.created_at)}</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Leave Policy Management */}
      {activeTab === 'leave-policy' && (
        <AdminLeaveManagement />
      )}

      {/* Leave Calendar */}
      {activeTab === 'leave-calendar' && (
        <LeaveCalendar />
      )}

      {/* Festival Suggestions — Admin review inbox for employee-proposed festival days */}
      {activeTab === 'festival-suggestions' && (
        <FestivalSuggestionsAdmin />
      )}

      {/* Customize Balances — single source of truth leave adjustment UI */}
      {activeTab === 'customize-balances' && (
        <CustomizeBalancesTab />
      )}

      {/* ========== CREATE/EDIT ROLE MODAL ========== */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Hiring Role' : 'Create Hiring Role'}</DialogTitle>
            <DialogDescription>
              {editingRole ? 'Update the role details below' : 'Fill in the details to create a new hiring role'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">
                  Designation <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g., Senior Developer"
                  value={roleForm.designation}
                  onChange={(e) => setRoleForm({ ...roleForm, designation: e.target.value })}
                  data-testid="role-designation-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">
                  Department <span className="text-red-500">*</span>
                </label>
                <Select 
                  value={roleForm.department_id} 
                  onValueChange={(val) => setRoleForm({ ...roleForm, department_id: val })}
                >
                  <SelectTrigger data-testid="role-department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map(dept => (
                      <SelectItem key={dept._id} value={dept._id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Qualifications</label>
              <Textarea
                placeholder="Required qualifications for this role..."
                value={roleForm.qualifications}
                onChange={(e) => setRoleForm({ ...roleForm, qualifications: e.target.value })}
                rows={3}
                data-testid="role-qualifications-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Skills</label>
              <Textarea
                placeholder="Required skills for this role..."
                value={roleForm.skills}
                onChange={(e) => setRoleForm({ ...roleForm, skills: e.target.value })}
                rows={3}
                data-testid="role-skills-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Scope of Work</label>
              <Textarea
                placeholder="Description of responsibilities and scope..."
                value={roleForm.scope_of_work}
                onChange={(e) => setRoleForm({ ...roleForm, scope_of_work: e.target.value })}
                rows={4}
                data-testid="role-scope-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="publish-role"
                checked={roleForm.is_published}
                onChange={(e) => setRoleForm({ ...roleForm, is_published: e.target.checked })}
                className="w-4 h-4 text-pink-600 rounded border-white/40"
              />
              <label htmlFor="publish-role" className="text-sm text-neutral-700">
                Publish this role (visible to applicants)
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRole}
              disabled={savingRole}
              className="bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="save-role-btn"
            >
              {savingRole ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== APPLICATION DETAIL MODAL ========== */}
      <Dialog open={showAppDetail} onOpenChange={setShowAppDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedApp && (
            <>
              <DialogHeader className="flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedApp.applicant_name}</DialogTitle>
                    <p className="text-neutral-500 text-sm mt-1">{selectedApp.applicant_email}</p>
                    {selectedApp.applicant_phone && (
                      <p className="text-neutral-400 text-sm">{selectedApp.applicant_phone}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 relative z-50">
                    <Select value={selectedApp.status} onValueChange={handleStatusChange}>
                      <SelectTrigger className={`w-40 ${getStatusColor(selectedApp.status)}`} data-testid="status-dropdown">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="Submitted">Submitted</SelectItem>
                        <SelectItem value="In Review">In Review</SelectItem>
                        <SelectItem value="Interview">Interview</SelectItem>
                        <SelectItem value="Offer Extended">Offer Extended</SelectItem>
                        <SelectItem value="Offer Accepted">Offer Accepted</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => setAppToDelete(selectedApp)}
                      className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                      title="Delete Application"
                      data-testid="delete-app-detail-btn"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 gap-6 py-4">
                  {/* Left Column - Application Details */}
                  <div className="space-y-4">
                    <div className="bg-white/40 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1a1a2e] mb-3">Position Applied</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Designation</span>
                          <span className="font-medium">{selectedApp.designation}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Department</span>
                          <span className="font-medium">{selectedApp.department_name}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/40 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1a1a2e] mb-3">Compensation</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Current CTC</span>
                          <span className="font-medium">{selectedApp.current_ctc || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Expected CTC</span>
                          <span className="font-medium text-green-600">₹{selectedApp.expected_ctc}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Notice Period</span>
                          <span className="font-medium">{selectedApp.notice_period || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/40 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1a1a2e] mb-3">Experience</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Years of Experience</span>
                          <span className="font-medium">{selectedApp.years_of_experience || 'N/A'}</span>
                        </div>
                        {selectedApp.past_experience && (
                          <div className="mt-2">
                            <span className="text-neutral-500 block mb-1">Past Experience</span>
                            <p className="text-neutral-700 whitespace-pre-wrap">{selectedApp.past_experience}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Portfolio Links */}
                    {(selectedApp.portfolio_urls?.length > 0 || selectedApp.portfolio_files?.length > 0) && (
                      <div className="bg-white/40 rounded-xl p-4">
                        <h4 className="font-semibold text-[#1a1a2e] mb-3">Portfolio</h4>
                        <div className="space-y-2">
                          {selectedApp.portfolio_urls?.filter(u => u).map((url, idx) => (
                            <a
                              key={idx}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                              <LinkIcon className="w-4 h-4" />
                              <span className="truncate">{url}</span>
                            </a>
                          ))}
                          {selectedApp.portfolio_files?.map((file, idx) => (
                            <a
                              key={idx}
                              href={file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                              <Paperclip className="w-4 h-4" />
                              <span>Attachment {idx + 1}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Offer Action */}
                    {selectedApp.status !== 'Offer Extended' && selectedApp.status !== 'Offer Accepted' && (
                      <Button
                        onClick={() => setShowOfferModal(true)}
                        className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90 text-white"
                        data-testid="extend-offer-btn"
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        Extend Offer
                      </Button>
                    )}

                    {selectedApp.offer_amount && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Offer Details</h4>
                        <p className="text-2xl font-bold text-green-700">₹{selectedApp.offer_amount}</p>
                        <p className="text-sm text-green-600 mt-1">
                          Status: {selectedApp.offer_status || 'Pending'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Chat */}
                  <div className="flex flex-col bg-white/40 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-neutral-100 border-b border-white/40 flex items-center justify-between">
                      <h4 className="font-semibold text-[#1a1a2e] flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Chat with Applicant
                      </h4>
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="hr-chat-live-indicator">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                        Live · refreshing every 5s
                      </span>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]">
                      {selectedApp.messages?.length === 0 ? (
                        <p className="text-center text-neutral-500 py-8">No messages yet</p>
                      ) : (
                        selectedApp.messages?.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex ${msg.sender_type === 'admin' || msg.sender_type === 'system' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-xl px-4 py-2 ${
                                msg.sender_type === 'system'
                                  ? 'bg-purple-100 text-purple-800'
                                  : msg.sender_type === 'admin'
                                  ? 'bg-pink-600 text-white'
                                  : 'glass-card text-[#1a1a2e]'
                              }`}
                            >
                              {msg.is_offer && (
                                <div className="flex items-center gap-2 mb-2">
                                  <Gift className="w-4 h-4" />
                                  <span className="font-semibold">Job Offer</span>
                                </div>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                              <p className={`text-xs mt-1 ${
                                msg.sender_type === 'admin' ? 'text-pink-200' : 'text-neutral-400'
                              }`}>
                                {msg.sender_name} • {formatDateTime(msg.timestamp)}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <div className="p-4 bg-white border-t border-white/40">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                          data-testid="chat-message-input"
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={sendingMessage || !newMessage.trim()}
                          className="bg-pink-600 hover:bg-pink-700 text-white"
                          data-testid="send-message-btn"
                        >
                          {sendingMessage ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ========== EXTEND OFFER MODAL ========== */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Extend Job Offer</DialogTitle>
            <DialogDescription>
              Enter the annual CTC to offer {selectedApp?.applicant_name}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Annual CTC (₹)
            </label>
            <Input
              type="text"
              placeholder="e.g., 12,00,000"
              value={offerAmount}
              onChange={(e) => setOfferAmount(e.target.value)}
              data-testid="offer-amount-input"
            />
            <p className="text-xs text-neutral-500 mt-2">
              This will send an automated offer message to the applicant
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExtendOffer}
              disabled={sendingOffer || !offerAmount}
              className="bg-gradient-to-r from-pink-500 to-purple-600 text-white"
              data-testid="confirm-offer-btn"
            >
              {sendingOffer ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Extend Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Application Confirmation Dialog */}
      <AlertDialog open={!!appToDelete} onOpenChange={(open) => !open && setAppToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this application? This action cannot be undone.
              <div className="mt-3 p-3 bg-white/40 rounded-xl">
                <p className="font-medium text-[#1a1a2e]">{appToDelete?.applicant_name}</p>
                <p className="text-sm text-neutral-600">{appToDelete?.applicant_email}</p>
                <p className="text-sm text-neutral-500">Applied for: {appToDelete?.designation}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingApp}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteApplication}
              disabled={deletingApp}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deletingApp ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
